import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import CustomReactSelect from "components/CustomReactSelect";
import DataTable from "components/DataTable";
import HeaderCard from "components/DataTable/HeaderCard";
import Loading from "components/Loading";
import MDBox from "components/MDBox";
import RideSelector from "components/RideSelector";
import { searchBoxStyles } from "globalStyles";
import debounce from 'lodash/debounce';
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import useFilterStore from "store/useFilterStore";
import { getChecklistCategories, getFrequencyFilters, getStatusFilters } from "utils/helper";

const PAGE_SIZE = 20;
const STATUS_FILTERS = getStatusFilters()
const frequencyTypes = getFrequencyFilters(true)
const checklistCategories = getChecklistCategories();

function BaseTable({
    title,
    columns,
    fetchData,
    createTableRow,
    showAddButton = false,
    addButtonText = "Add New",
    onAddClick,
    showSearch = false,
    searchPlaceholder = "Search...",
    defaultState = {},
    showStatusFilter = false,
    showRideFilter = false,
    showFrequencyTypeFilter = false,
    showInspectionCategoryFilter = false,
    additionalFilters = null,
    onClickRow,
    customButton = null,
    tableOnly = false,
}) {
    const { getFilters, setFilters } = useFilterStore();

    // Get current pathname for storage key
    const currentPath = window.location.pathname;

    // Load filters from Zustand store on component mount
    const getInitialState = useCallback(() => {
        const record = getFilters(currentPath);
        const stored = record?.baseTable || {};
        if (!record.pathname) {
            return {
                page: 1,
                per_page: PAGE_SIZE,
                sort: 'created_at',
                dir: 'desc',
                ...defaultState
            };
        }
        return { ...stored, ...defaultState };
    }, [defaultState, currentPath, getFilters]);


    const [tableData, setTableData] = useState({ columns, rows: [] });
    const [isLoading, setIsLoading] = useState(true);
    const [paging, setPaging] = useState({ page: 1 });

    // Single params state for all filters
    const initialState = getInitialState();
    const [params, setParams] = useState(initialState);

    const fetchTableData = useCallback(async (search = "") => {
        setIsLoading(true);
        try {
            let paramsForApi = {
                ...(search ? { search } : {}),
                ...params
            }
            const response = await fetchData(paramsForApi);
            setPaging(response.data?.paging);
            if (currentPath.split('/').length <= 2) {
                setFilters(currentPath, {
                    baseTable: {
                        ...paramsForApi
                    }
                });
            }

            setTableData(prevData => ({
                ...prevData,
                rows: response.data?.data
                    ?.map(item => ({
                        id: item.id,
                        oriData: item,
                        ...createTableRow(item)
                    }))
            }));
        } catch (error) {
            toast.error(error || 'Failed to fetch data');
        } finally {
            setIsLoading(false);
        }
    }, [params, fetchData, setFilters, currentPath, createTableRow]);

    const debouncedFetchData = useMemo(() => debounce((search, selectedRide) => {
        fetchTableData(search, selectedRide);
    }, 700), [fetchTableData]);

    // Fetch data when filters change - MINIMAL DEPENDENCIES
    useEffect(() => {
        debouncedFetchData(params.search, params.ride_id);
        return () => {
            debouncedFetchData.cancel();
        }
    }, [params.search, params.ride_id, debouncedFetchData]);

    const handleSearchChange = (value) => {
        setParams(prev => ({ ...prev, search: value, page: 1 }));
    };

    const handlePageChange = (newPage) => {
        setParams(prev => ({ ...prev, page: newPage }));
    };

    const handlePageSizeChange = (newSize) => {
        setParams(prev => ({ ...prev, per_page: newSize, page: 1 }));
    };

    const handleSortChange = (columnId, direction) => {
        setParams(prev => ({ ...prev, sort: columnId, dir: direction, page: 1 }));
    };


    const content = (
        <>
            {!tableOnly && <HeaderCard
                title={title}
                showButton={showAddButton}
                buttonText={addButtonText}
                onButtonClick={onAddClick}
            />}
            <MDBox pt={tableOnly ? 1 : 3}>
                <MDBox mb={3} px={2}>
                    <MDBox sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {showSearch && (
                            <MDBox
                                component="input"
                                placeholder={searchPlaceholder}
                                sx={{
                                    flex: 2.2,
                                    ...searchBoxStyles
                                }}
                                value={params.search ?? ""}
                                onChange={(e) => handleSearchChange(e.target.value)}
                            />
                        )}
                        {showInspectionCategoryFilter && <MDBox sx={{ flex: 1, minWidth: 190 }}>
                            <CustomReactSelect
                                label="Category"
                                name="category"
                                value={checklistCategories.find(cat => cat.value === params.category)}
                                onChange={option => setParams(prev => ({ ...prev, category: option?.value, page: 1 }))}
                                options={checklistCategories}
                                isClearable
                            />
                        </MDBox>}
                        {additionalFilters}
                        {showFrequencyTypeFilter && <MDBox sx={{ flex: 1, minWidth: 140 }}>
                            <CustomReactSelect
                                label="Type"
                                name="type"
                                placeholder="All Type"
                                value={frequencyTypes.find(type => type.value === params.type)}
                                onChange={option => setParams(prev => ({ ...prev, type: option?.value, page: 1 }))}
                                options={frequencyTypes}
                                isClearable
                            />
                        </MDBox>}
                        {showRideFilter && <MDBox sx={{ flex: 1.4, minWidth: 200 }}>
                            <RideSelector
                                key={params.ride_id}
                                label="Ride"
                                name="ride"
                                value={params.ride_id}
                                onChange={option => { setParams(prev => ({ ...prev, ride_id: option, page: 1 })) }}
                                isClearable
                            />
                        </MDBox>}

                        {showStatusFilter && <MDBox sx={{ flex: 1, minWidth: 150 }}>
                            <CustomReactSelect
                                label="Status"
                                name="status"
                                value={STATUS_FILTERS.find(option => option.value === params.status)}
                                onChange={(selectedOption) => setParams(prev => ({ ...prev, status: selectedOption?.value, page: 1 }))}
                                options={STATUS_FILTERS}
                                isClearable
                            />
                        </MDBox>}
                        {customButton}
                    </MDBox>
                </MDBox>
                <MDBox position="relative" width="100%">
                    {isLoading && (
                        <MDBox
                            position="absolute"
                            top={0}
                            left={0}
                            width="100%"
                            height="100%"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                background: 'inherit',
                                zIndex: 10,
                                borderRadius: 2,
                            }}
                        >
                            <Loading />
                        </MDBox>
                    )}
                    {paging?.total > 0 ? (
                        <DataTable
                            table={tableData}
                            isSorted={true}
                            noEndBorder
                            page={paging.page}
                            pageSize={paging.per_page}
                            totalCount={paging.total}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                            sortBy={params.sort}
                            sortType={params.dir}
                            onSortChange={handleSortChange}
                            onClickRow={onClickRow}
                        />
                    ) : (
                        <MDBox
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            minHeight="200px"
                        >
                            {!isLoading && "No data found"}
                        </MDBox>
                    )}
                </MDBox>
            </MDBox>
        </>
    );

    return (
        <MDBox pt={4} pb={3}>
            <Grid container spacing={6}>
                <Grid item xs={12}>
                    {tableOnly ? content : <Card>{content}</Card>}
                </Grid>
            </Grid>
        </MDBox>
    );
}

BaseTable.propTypes = {
    title: PropTypes.string,
    columns: PropTypes.array.isRequired,
    fetchData: PropTypes.func.isRequired,
    createTableRow: PropTypes.func.isRequired,
    showAddButton: PropTypes.bool,
    addButtonText: PropTypes.string,
    onAddClick: PropTypes.func,
    showSearch: PropTypes.bool,
    searchPlaceholder: PropTypes.string,
    showRideFilter: PropTypes.bool,
    showFrequencyTypeFilter: PropTypes.bool,
    defaultState: PropTypes.object,
    storageKey: PropTypes.string,
    additionalFilters: PropTypes.node,
    onClickRow: PropTypes.func,
    customButton: PropTypes.node,
    tableOnly: PropTypes.bool
};

export default BaseTable;